import { useAuth0 } from '@auth0/auth0-react';
import * as icons from '@mui/icons-material';
import { Box, CssBaseline, GlobalStyles } from '@mui/material';
import { Authenticated, Refine, useWarnAboutChange } from '@refinedev/core';
import { RefineKbar, RefineKbarProvider } from '@refinedev/kbar';
import { ErrorComponent, RefineSnackbarProvider, ThemedLayoutV2 } from '@refinedev/mui';
import { useNotificationProvider } from '@refinedev/mui';
import routerBindings, {
  CatchAllNavigate,
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from '@refinedev/react-router';
import { useEffect, useMemo, useRef } from 'react';
import { BrowserRouter, Outlet, Route, Routes } from 'react-router';
import { Claims, parseRoles } from './auth.ts';
import { guardState } from './browser-back-guard-state.ts';
import { Header } from './components/index.ts';
import { ColorModeContextProvider } from './contexts/color-mode/index.tsx';
import { useAuthProvider } from './hooks/auth.ts';
import { useRestProvider } from './hooks/dataProvider.ts';
import Accounts from './pages/accounts/index.tsx';
import Contents from './pages/contents/index.tsx';
import { Login } from './pages/login.tsx';
import Organization from './pages/organization/$id/index.tsx';

// 未保存の変更がある場合に、ブラウザの戻る/進む（POP）を警告でガードする。
// popstate の遮断は init.client.ts のグローバルリスナーが担う（react-router より先に登録）。
// このコンポーネントは guardState の更新と beforeunload 管理のみを行う。
function BrowserBackUnsavedGuard({ message }: { message: string }) {
  const { warnWhen } = useWarnAboutChange();
  const historyGoInProgressRef = useRef(false);

  // message を guardState に同期（マウント時に設定、アンマウント時にリセット）
  useEffect(() => {
    guardState.message = message;
    return () => {
      guardState.message = '';
    };
  }, [message]);

  // warnWhen を guardState.isActive に同期
  // warnWhen=true になった時点で lastIdx も初期化する
  useEffect(() => {
    guardState.isActive = warnWhen;
    if (warnWhen) {
      guardState.lastIdx = (window.history.state?.idx as number | null | undefined) ?? null;
    }
  }, [warnWhen]);

  // beforeunload 管理: history.go() 中（suppress 後の popstate 復元中）は発火しない
  useEffect(() => {
    if (!warnWhen) return;

    // history.go() 呼び出し中は beforeunload を発火しないよう history.go をパッチ
    const origGo = window.history.go.bind(window.history);
    window.history.go = (delta?: number) => {
      historyGoInProgressRef.current = true;
      const result = origGo(delta);
      Promise.resolve().then(() => {
        historyGoInProgressRef.current = false;
      });
      return result;
    };

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (historyGoInProgressRef.current) return;
      e.preventDefault();
      e.returnValue = message;
    };

    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.history.go = origGo;
    };
  }, [warnWhen, message]);

  return null;
}

function App() {
  const { user } = useAuth0();
  const roles = useMemo(() => (user ? parseRoles(Claims.parse(user)) : null), [user]);

  const authProvider = useAuthProvider();
  const dataProvider = useRestProvider(new URL('/.api/v1', window.location.origin));

  if (!authProvider) {
    return (
      <div
        style={{ height: '100vh', display: 'flex', justifySelf: 'center', alignItems: 'center' }}
      >
        Loading...
      </div>
    );
  }

  return (
    <BrowserRouter>
      <ColorModeContextProvider>
        <RefineSnackbarProvider>
          <RefineKbarProvider>
            <CssBaseline />
            <GlobalStyles styles={{ html: { WebkitFontSmoothing: 'auto' } }} />
            <Refine
              dataProvider={dataProvider}
              notificationProvider={useNotificationProvider}
              authProvider={authProvider}
              routerProvider={routerBindings}
              resources={[
                ...(roles?.cms?.type === 'admin'
                  ? [
                      {
                        name: 'contents/documents',
                        list: '/contents/documents',
                        create: '/contents/documents/create',
                        edit: '/contents/documents/edit/:id',
                        show: '/contents/documents/show/:id',
                        meta: {
                          canDelete: false,
                          label: '記事',
                          icon: <icons.ArticleOutlined />,
                        },
                      },
                      {
                        name: 'contents/pickup',
                        list: '/contents/pickup',
                        meta: {
                          canDelete: false,
                          label: 'ピックアップ',
                          icon: <icons.StarBorderOutlined />,
                        },
                      },
                      {
                        name: 'contents/authors',
                        list: '/contents/authors',
                        create: '/contents/authors/create',
                        edit: '/contents/authors/edit/:id',
                        show: '/contents/authors/show/:id',
                        meta: {
                          canDelete: false,
                          label: '執筆者',
                          icon: <icons.PeopleAltOutlined />,
                        },
                      },
                      {
                        name: 'contents/timelines',
                        list: '/contents/timelines',
                        create: '/contents/timelines/create',
                        edit: '/contents/timelines/edit/:id',
                        show: '/contents/timelines/show/:id',
                        meta: {
                          canDelete: false,
                          label: 'タイムライン',
                          icon: <icons.TimelineOutlined />,
                        },
                      },
                      {
                        name: 'contents/sectors',
                        list: '/contents/sectors',
                        create: '/contents/sectors/create',
                        edit: '/contents/sectors/edit/:id',
                        meta: {
                          canDelete: false,
                          label: '分野',
                          icon: <icons.CategoryOutlined />,
                        },
                      },
                      {
                        name: 'contents/topics',
                        list: '/contents/topics',
                        create: '/contents/topics/create',
                        edit: '/contents/topics/edit/:id',
                        meta: {
                          canDelete: false,
                          label: 'トピック',
                          icon: <icons.LabelOutlined />,
                        },
                      },
                      {
                        name: 'contents/series',
                        list: '/contents/series',
                        create: '/contents/series/create',
                        edit: '/contents/series/edit/:id',
                        meta: {
                          canDelete: false,
                          label: 'シリーズ',
                          icon: <icons.LibraryBooksOutlined />,
                        },
                      },
                    ]
                  : []),

                ...(roles?.account?.type === 'admin'
                  ? [
                      {
                        name: 'accounts/organizations',
                        list: '/accounts/organizations',
                        create: '/accounts/organizations/create',
                        edit: '/accounts/organizations/edit/:id',
                        show: '/accounts/organizations/show/:id',
                        meta: {
                          canDelete: false,
                          label: '組織',
                          icon: <icons.BusinessOutlined />,
                        },
                      },
                    ]
                  : []),

                ...(roles?.organization?.type === 'admin'
                  ? roles.organization.isDangerousSuperOrganizationAdmin
                    ? [
                        {
                          name: `organization/:organizationId/accounts`,
                          list: `/organization/:organizationId/accounts`,
                          create: `/organization/:organizationId/accounts/create`,
                          edit: `/organization/:organizationId/accounts/edit/:id`,
                          show: `/organization/:organizationId/accounts/show/:id`,
                          meta: {
                            canDelete: false,
                            label: '利用者',
                            icon: <icons.Person />,
                            hide: true,
                          },
                        },
                      ]
                    : roles.organization.ids.map((id) => ({
                        name: `organization/${id}/accounts`,
                        list: `/organization/${id}/accounts`,
                        create: `/organization/${id}/accounts/create`,
                        edit: `/organization/${id}/accounts/edit/:id`,
                        show: `/organization/${id}/accounts/show/:id`,
                        meta: {
                          canDelete: false,
                          label: '利用者',
                          icon: <icons.Person />,
                          hide: true,
                        },
                      }))
                  : []),
              ]}
              options={{
                syncWithLocation: true,
                warnWhenUnsavedChanges: true,
                useNewQueryKeys: true,
                projectId: 'deadbeef',
                title: { text: '管理画面', icon: <icons.CreateOutlined /> },
              }}
            >
              <Routes>
                <Route
                  element={
                    <Authenticated
                      key="authenticated-inner"
                      fallback={<CatchAllNavigate to="/login" />}
                    >
                      <Outlet />
                    </Authenticated>
                  }
                >
                  <Route
                    index
                    element={
                      <ThemedLayoutV2 Header={Header} dashboard>
                        <Box display="flex" justifyContent="center" fontSize={36}>
                          welcome
                        </Box>
                      </ThemedLayoutV2>
                    }
                  />
                  <Route path="/accounts/*" element={<Accounts />} />
                  <Route path="/contents/*" element={<Contents />} />
                  <Route path="/organization">
                    <Route path="/organization/:organizationId/*" element={<Organization />} />
                    <Route path="*" element={<ErrorComponent />} />
                  </Route>
                </Route>
                <Route
                  element={
                    <Authenticated key="authenticated-outer" fallback={<Outlet />}>
                      <NavigateToResource />
                    </Authenticated>
                  }
                >
                  <Route path="/login" element={<Login />} />
                </Route>

                <Route path="*" element={<ErrorComponent />} />
              </Routes>

              <RefineKbar />
              <UnsavedChangesNotifier message="作業中の内容が破棄されます。よろしいですか？" />
              <BrowserBackUnsavedGuard message="作業中の内容が破棄されます。よろしいですか？" />
              <DocumentTitleHandler />
            </Refine>
          </RefineKbarProvider>
        </RefineSnackbarProvider>
      </ColorModeContextProvider>
    </BrowserRouter>
  );
}

export default App;
